/**
 * Settings, You, Notifications: the person's own email digest, quiet hours and whether records
 * for them in particular count (shared/schemas/notificationPreferences.ts). Calm: rows in one group,
 * each choice kept as it is made, as the workbench's Settings do.
 */

import type { NotificationPreferences, NotificationPreferencesInput } from '@aioe/shared';
import { timeOfDay } from '@aioe/shared/wording/dateTime';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow, selectClass, Toggle } from './ui.tsx';

const CADENCE = { off: 'Off', hourly: 'Every hour', daily: 'Once a day' } as const;
const hourOptions = (): Record<string, string> => Object.fromEntries(Array.from({ length: 24 }, (_, h) => [String(h), timeOfDay(h, 0)]));
const timeOptions = (): Record<string, string> => Object.fromEntries(Array.from({ length: 24 }, (_, h) => [`${String(h).padStart(2, '0')}:00`, timeOfDay(h, 0)]));

export function NotificationPreferencesPanel() {
  const loaded = useLoad(() => routes.notificationPreferences().catch(() => undefined), []);
  if (!loaded.data) return null;
  return <NotificationPreferencesForm p={loaded.data} reload={loaded.reload} />;
}

function NotificationPreferencesForm({ p, reload }: { p: NotificationPreferences; reload: () => void }) {
  const [prefs, setPrefs] = useState<NotificationPreferencesInput>({ digest: p.digest, quietHours: p.quietHours, ...(p.timeZone ? { timeZone: p.timeZone } : {}), forYou: p.forYou });
  const [zone, setZone] = useState(p.timeZone ?? '');
  const [error, setError] = useState<string>();
  // Each choice is kept as it is made; a refusal puts the previous one back.
  const keep = async (next: NotificationPreferencesInput) => {
    const before = prefs;
    setPrefs(next);
    setError(undefined);
    try {
      await routes.setNotificationPreferences(next);
      reload();
    } catch (e) {
      setPrefs(before);
      setError((e as Error).message);
    }
  };
  const keepZone = () => {
    const z = zone.trim();
    if (z === (prefs.timeZone ?? '')) return;
    const { timeZone: _t, ...rest } = prefs;
    void keep(z ? { ...rest, timeZone: z } : rest);
  };
  return (
    <ListGroup id="notifications" description="What reaches you when you are not looking at the console. What needs you is always in your inbox.">
      <ListRow
        title="Email digest"
        description={
          <>
            {p.email ? `One email to ${p.email} listing what is open for you.` : 'One email listing what is open for you.'} Nothing is sent when nothing is waiting.
            {p.emailAvailable ? '' : ' This service has no mail server set up yet, so nothing will go until its administrator adds one.'}
          </>
        }
        control={
          <>
            <ChoiceSelect label="Email digest" value={prefs.digest.cadence} options={CADENCE} onChange={(cadence) => void keep({ ...prefs, digest: { ...prefs.digest, cadence } })} className="w-36" />
            {prefs.digest.cadence === 'daily' ? <ChoiceSelect label="Hour the digest goes" value={String(prefs.digest.hour)} options={hourOptions()} onChange={(h) => void keep({ ...prefs, digest: { ...prefs.digest, hour: Number(h) } })} className="w-28" /> : null}
          </>
        }
      />
      <ListRow
        title="Quiet hours"
        description={`Your phone stays quiet. What needs you waits in your inbox, and one message tells you what came in when the hours end.${p.quietNow ? ' Quiet now.' : ''}`}
        control={<Toggle label="Quiet hours" checked={prefs.quietHours.enabled} onChange={(enabled) => void keep({ ...prefs, quietHours: { ...prefs.quietHours, enabled } })} />}
      >
        {prefs.quietHours.enabled ? (
          <div className="flex flex-wrap items-center gap-2">
            <ChoiceSelect label="Quiet from" value={prefs.quietHours.start} options={timeOptions()} onChange={(start) => void keep({ ...prefs, quietHours: { ...prefs.quietHours, start } })} className="w-28" />
            <span className="text-muted">to</span>
            <ChoiceSelect label="Quiet until" value={prefs.quietHours.end} options={timeOptions()} onChange={(end) => void keep({ ...prefs, quietHours: { ...prefs.quietHours, end } })} className="w-28" />
          </div>
        ) : null}
      </ListRow>
      <ListRow
        title="Time zone"
        htmlFor="aioe-time-zone"
        description={`Quiet hours and the digest’s hour use it. Left empty, it is the organisation’s (${p.organisationTimeZone}).`}
        control={
          <>
            <input
              id="aioe-time-zone"
              list="aioe-time-zones"
              className={`${selectClass} w-56 max-w-full`}
              placeholder={p.organisationTimeZone}
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              onBlur={keepZone}
              onKeyDown={(e) => {
                if (e.key === 'Enter') keepZone();
              }}
            />
            <datalist id="aioe-time-zones">{(typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []).map((z) => <option key={z} value={z} />)}</datalist>
          </>
        }
      />
      <ListRow
        title="Updates addressed to you"
        description="What needs you is always included. Turn this on to hear about updates addressed to you as well."
        control={<Toggle label="Updates addressed to you" checked={prefs.forYou} onChange={(forYou) => void keep({ ...prefs, forYou })} />}
      />
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
